/**
 * Server-paginated affiliate list for the Active tab.
 * Uses limit/offset pagination (not page/page_size). Mirrors
 * CustomerListSection structure: Toolbar → DataTable → Pagination.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Handshake } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type FilterCondition, type FilterField } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useAffiliates, type AffiliateListParams } from '@/api/affiliate';
import { paginationView } from '@/pages/customers/pagination-summary';
import { AFFILIATE_ACTIVE_COLUMNS } from '../list-configs/affiliate-column-definitions';
import { AffiliateListToolbar } from './affiliate-list-toolbar';
import { AFFILIATE_STATUS_OPTIONS, AFFILIATE_TIER_OPTIONS } from '../list-configs/affiliate-filter-constants';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// FilterField extends FilterOption: `value` is the field key, `label` is display name.
const FILTER_FIELDS: FilterField[] = [
  { value: 'status', label: 'Status', input: 'select', options: AFFILIATE_STATUS_OPTIONS },
  { value: 'tier', label: 'Tier', input: 'select', options: AFFILIATE_TIER_OPTIONS },
];

const ALL_COLUMNS = AFFILIATE_ACTIVE_COLUMNS;

interface AffiliateActiveListSectionProps {
  /** When set, scopes the list to referrer_type (e.g. 'non_employee' or 'employee'). */
  referrerType?: AffiliateListParams['referrer_type'];
}

export function AffiliateActiveListSection({ referrerType }: AffiliateActiveListSectionProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(ALL_COLUMNS.map((c) => [c.id as string, true])),
  );

  const offset = (page - 1) * PAGE_SIZE;

  /* Status/Tier are server-side filters — sent as plain query params like legacy. */
  const statusFilter = conditions.find((c) => c.field === 'status')?.value;
  const tierFilter = conditions.find((c) => c.field === 'tier')?.value;

  const params: AffiliateListParams = {
    limit: PAGE_SIZE,
    offset,
    ...(query ? { search: query } : {}),
    ...(referrerType ? { referrer_type: referrerType } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(tierFilter ? { tier: tierFilter } : {}),
  };

  const { data, isLoading, isError, refetch, isFetching } = useAffiliates(params);

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;

  const visibleColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => visible[c.id as string] !== false),
    [visible],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <AffiliateListToolbar
        columns={ALL_COLUMNS}
        filterFields={FILTER_FIELDS}
        searchPlaceholder="Search affiliates…"
        query={rawQuery}
        onQueryChange={onSearch}
        conditions={conditions}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        onApplyFilters={applyFilters}
        visible={visible}
        onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load affiliates"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={(r) => String(r.affiliate_id)}
          onRowClick={(r) => navigate(`/affiliates/${r.affiliate_id}`)}
          emptyState={
            <EmptyState
              icon={<Handshake size={24} />}
              title="No affiliates found"
              description="Try adjusting your search or filters."
            />
          }
          footer={
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={`${view.summary}${isFetching ? ' …' : ''}`}
            />
          }
        />
      )}
    </div>
  );
}
